import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'foundations-components/components/Text';
import { getIsIframePreviewUrl, getIsPaymentPurchaseUrl } from '../../utils/urls';
import { getIsAnyRecurring } from '../../utils/recurring';
import RecurringTotalsMessage from '../RecurringTotalsMessage';
import { hasCustomConfiguration, isOptional } from '../../types/utils/typeguards';
import { getLineItemBlurs } from '../../state/lineItemBlursSlice';
import { getShowAllErrors, setErrorMessages } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import useMinMaxAmountsFromValidationErrors from '../../hooks/useMinMaxAmountsFromValidationErrors';
import { useValidationErrors } from '../../hooks/useValidationErrors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TotalsValidationMessages = ({
  checkoutSession,
  selectedOptionalLineItems
}) => {
  const dispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const lineItemBlurs = useSelector(getLineItemBlurs);
  const {
    pathname
  } = useLocation();
  const shouldShowRecurringMessage = (getIsPaymentPurchaseUrl(pathname) || getIsIframePreviewUrl(pathname)) && getIsAnyRecurring(checkoutSession.lineItems, selectedOptionalLineItems) && checkoutSession.lineItems.every(lineItem => lineItem.billedAtCheckout);
  const shouldShowAtCheckoutRecurringMessage = showAllErrors || checkoutSession.lineItems.some(lineItem => lineItem.billedAtCheckout && !!lineItem.recurringBillingTerms && lineItemBlurs[lineItem.id]);
  const shouldShowAtCheckoutNonRecurringMessage = showAllErrors || checkoutSession.lineItems.some(lineItem => lineItem.billedAtCheckout && !lineItem.recurringBillingTerms && lineItemBlurs[lineItem.id]);
  const {
    subtotalsValidationErrors
  } = useValidationErrors();
  const filteredErrors = useMemo(() => subtotalsValidationErrors.filter(error => {
    var _error$context;
    return !((_error$context = error.context) !== null && _error$context !== void 0 && (_error$context = _error$context.initialPaymentType) !== null && _error$context !== void 0 && _error$context.includes('FUTURE_START_DATE'));
  }), [subtotalsValidationErrors]);
  const {
    minimumAmount,
    maximumAmount,
    isOrderTotalAboveMax,
    isOrderTotalBelowMin,
    isRecurringTotalBelowMin,
    isRecurringTotalAboveMax
  } = useMinMaxAmountsFromValidationErrors({
    validationErrors: filteredErrors,
    shouldShowAtCheckout: shouldShowAtCheckoutNonRecurringMessage,
    shouldShowRecurring: shouldShowAtCheckoutRecurringMessage
  });
  const areAllItemsOptional = checkoutSession.lineItems.every(l => hasCustomConfiguration(l) && isOptional(l));
  const areAllOptionalLineItemsUnselected = Boolean(areAllItemsOptional && (selectedOptionalLineItems === null || selectedOptionalLineItems === void 0 ? void 0 : selectedOptionalLineItems.every(isSelected => isSelected === false)));
  const currencyCode = checkoutSession.currencyCode;
  useEffect(() => {
    dispatch(setErrorMessages({
      totalOverMax: {
        isError: isOrderTotalAboveMax || isRecurringTotalAboveMax,
        totalsOptions: {
          currencyCode,
          maximumAmount
        }
      },
      totalBelowMin: {
        isError: isOrderTotalBelowMin,
        totalsOptions: {
          currencyCode,
          minimumAmount
        }
      },
      recurringDueNowTotalBelowMin: {
        isError: !isOrderTotalBelowMin && isRecurringTotalBelowMin,
        totalsOptions: {
          currencyCode,
          minimumAmount
        }
      },
      allOptionalItemsUnselected: {
        isError: areAllOptionalLineItemsUnselected
      }
    }));
  }, [areAllOptionalLineItemsUnselected, currencyCode, dispatch, isOrderTotalAboveMax, isOrderTotalBelowMin, isRecurringTotalAboveMax, isRecurringTotalBelowMin, minimumAmount, maximumAmount]);
  const allOptionalItemsUnselectedMessage = useGetErrorMessagesAndTranslations().get('allOptionalItemsUnselected');
  const belowMinMessage = useGetErrorMessagesAndTranslations().get(!isOrderTotalBelowMin && isRecurringTotalBelowMin ? 'recurringTotalBelowMin' : 'totalBelowMin');
  const totalOverMaxMessage = useGetErrorMessagesAndTranslations().get('totalOverMax');
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [areAllOptionalLineItemsUnselected && showAllErrors && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      textAlign: "end",
      "data-test-id": "minimum-validation-optional-error-message",
      as: "p",
      typography: "detail-100",
      color: "text-alert-default",
      children: allOptionalItemsUnselectedMessage
    }), (isOrderTotalBelowMin || isRecurringTotalBelowMin) && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      "data-test-id": "minimum-validation-error-message",
      as: "p",
      typography: "detail-100",
      textAlign: "end",
      color: "text-alert-default",
      children: belowMinMessage
    }), (isOrderTotalAboveMax || isRecurringTotalAboveMax) && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      "data-test-id": "maximum-validation-error-message",
      as: "p",
      typography: "detail-100",
      textAlign: "end",
      color: "text-alert-default",
      children: totalOverMaxMessage
    }), shouldShowRecurringMessage && /*#__PURE__*/_jsx(Text, {
      paddingBlock: "100",
      as: "p",
      typography: "detail-100",
      color: "text-core-subtle",
      "data-test-id": "subsequent-payment-message",
      textAlign: "end",
      children: /*#__PURE__*/_jsx(RecurringTotalsMessage, {
        currencyCode: currencyCode
      })
    })]
  });
};
export default TotalsValidationMessages;